import React, { useState, useEffect } from 'react';
import {
  TrophyIcon,
  ChartBarIcon,
  AcademicCapIcon,
  ClockIcon,
  FireIcon,
  SparklesIcon,
  LockClosedIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  BookOpenIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoginPromptManager from '../auth/LoginPromptManager';
import CommonHeader from '../common/CommonHeader';
import './DiagnosticResults.css';

const DiagnosticResults = () => {
  const { examSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loginWithSocial } = useAuth();
  const [results, setResults] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);

  useEffect(() => {
    // location.state에서 결과 가져오기
    const resultsFromState = location.state?.results;
    
    if (resultsFromState) {
      setResults(resultsFromState);
      // 로그인된 경우 AI 분석 생성
      if (isAuthenticated) {
        generateAIAnalysis(resultsFromState);
      }
    } else {
      // 저장된 진단 결과 불러오기 (fallback)
      const savedResults = localStorage.getItem('diagnosticResults');
      if (savedResults) {
        const parsedResults = JSON.parse(savedResults);
        setResults(parsedResults);
        
        // 로그인된 경우 AI 분석 생성
        if (isAuthenticated) {
          generateAIAnalysis(parsedResults);
        }
      } else {
        // 결과가 없으면 홈으로
        navigate(`/${examSlug}`);
      }
    }
  }, [isAuthenticated, location.state, navigate]);

  // AI 분석 생성 (실제로는 서버에서 처리)
  const generateAIAnalysis = (resultData) => {
    // 모의 AI 분석 데이터
    const analysis = {
      weakestSubject: Object.entries(resultData.subjectScores).reduce((min, [subject, data]) => 
        data.percentage < (min.percentage || 100) ? { subject, ...data } : min
      , {}),
      
      strongestSubject: Object.entries(resultData.subjectScores).reduce((max, [subject, data]) => 
        data.percentage > (max.percentage || 0) ? { subject, ...data } : max
      , {}),
      
      studyPlan: {
        estimatedDays: Math.ceil((100 - resultData.score) * 0.8),
        dailyGoal: resultData.score < 60 ? 50 : 30,
        prioritySubjects: Object.entries(resultData.subjectScores)
          .sort((a, b) => a[1].percentage - b[1].percentage)
          .slice(0, 3)
          .map(([subject]) => subject),
        recommendedSchedule: resultData.score < 60 ? '주 6일, 일 3시간' : '주 5일, 일 2시간'
      },
      
      speedAnalysis: {
        avgTime: resultData.avgTimePerQuestion,
        comparison: resultData.avgTimePerQuestion < 30 ? '평균보다 빠름' : '평균보다 느림',
        advice: resultData.avgTimePerQuestion < 20 
          ? '정확도에 더 신경쓰세요' 
          : resultData.avgTimePerQuestion > 40 
            ? '속도를 높일 필요가 있습니다' 
            : '적절한 속도입니다'
      },
      
      passChance: resultData.score >= 70 ? '높음' : resultData.score >= 50 ? '보통' : '낮음',
      
      recommendations: [
        resultData.score < 60 && '기초 개념부터 차근차근 학습하세요',
        resultData.avgTimePerQuestion > 40 && '문제 풀이 속도를 높이는 연습이 필요합니다',
        '매일 꾸준히 학습하는 것이 중요합니다',
        '오답노트를 작성하여 실수를 줄이세요'
      ].filter(Boolean)
    };
    
    setAiAnalysis(analysis);
  };

  const handleLoginClick = async (provider) => {
    try {
      await loginWithSocial(provider);
      setShowLoginPrompt(false);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  if (!results) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreMessage = (score) => {
    if (score >= 80) return '훌륭합니다! 🎉';
    if (score >= 60) return '좋은 시작입니다! 👍';
    return '더 노력이 필요합니다 💪';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <CommonHeader 
        title="진단평가 결과"
        subtitle="AI가 분석한 당신의 실력과 학습 전략"
      />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* 점수 요약 (항상 표시) */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 mb-6">
              <TrophyIcon className="w-16 h-16 text-orange-600" />
            </div>
            
            <h2 className="text-5xl font-bold mb-2">
              <span className={getScoreColor(results.score)}>{results.score}점</span>
            </h2>
            
            <p className="text-2xl font-semibold text-gray-700 mb-2">
              {getScoreMessage(results.score)}
            </p>
            
            <p className="text-gray-600">
              {results.totalQuestions}문제 중 {results.correctCount}문제 정답
            </p>
            
            <div className="flex justify-center gap-8 mt-6">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <ClockIcon className="w-5 h-5" />
                  <span className="text-sm">소요 시간</span>
                </div>
                <p className="text-xl font-semibold mt-1">
                  {Math.floor(results.totalTime / 60)}분 {results.totalTime % 60}초
                </p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <ChartBarIcon className="w-5 h-5" />
                  <span className="text-sm">평균 시간</span>
                </div>
                <p className="text-xl font-semibold mt-1">
                  {results.avgTimePerQuestion}초/문제
                </p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <AcademicCapIcon className="w-5 h-5" />
                  <span className="text-sm">합격 가능성</span>
                </div>
                <p className="text-xl font-semibold mt-1">
                  {results.score >= 70 ? '높음' : results.score >= 50 ? '보통' : '낮음'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 과목별 점수 (항상 표시) */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h3 className="text-2xl font-bold mb-6 flex items-center">
            <ChartBarIcon className="w-7 h-7 mr-3 text-orange-500" />
            과목별 성적
          </h3>
          
          <div className="space-y-4">
            {Object.entries(results.subjectScores).map(([subject, data]) => (
              <div key={subject}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-700">{subject}</span>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">
                      {data.correct}/{data.total}
                    </span>
                    <span className={`font-semibold ${getScoreColor(data.percentage)}`}>
                      {data.percentage}%
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-500 ${
                      data.percentage >= 80 
                        ? 'bg-gradient-to-r from-green-400 to-green-600'
                        : data.percentage >= 60
                          ? 'bg-gradient-to-r from-yellow-400 to-yellow-600'
                          : 'bg-gradient-to-r from-red-400 to-red-600'
                    }`}
                    style={{ width: `${data.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>


        {/* AI 상세 분석 (블러 처리 또는 표시) */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* 취약점 분석 */}
          <div className={`bg-white rounded-2xl shadow-xl p-8 relative ${!isAuthenticated ? 'analysis-locked' : ''}`}>
            <h3 className="text-xl font-bold mb-6 flex items-center">
              <ExclamationTriangleIcon className="w-6 h-6 mr-3 text-red-500" />
              취약점 분석
            </h3>
            
            {isAuthenticated && aiAnalysis ? (
              <div>
                <div className="bg-red-50 rounded-xl p-4 mb-4">
                  <p className="font-semibold text-red-800 mb-2">
                    가장 취약한 과목: {aiAnalysis.weakestSubject.subject}
                  </p>
                  <p className="text-red-600">
                    정답률 {aiAnalysis.weakestSubject.percentage}%
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    📚 추천: 기초 개념부터 차근차근 재학습하세요
                  </p>
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">우선 학습 과목:</span>
                  </p>
                  <ol className="list-decimal list-inside space-y-1">
                    {aiAnalysis.studyPlan.prioritySubjects.map((subject, idx) => (
                      <li key={idx} className="text-gray-700">{subject}</li>
                    ))}
                  </ol>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-4">
                  <LockClosedIcon className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-gray-600 mb-2">🎯 당신의 취약점을 정확히 진단</p>
                <p className="text-sm text-gray-500">가장 약한 과목과 우선순위가...</p>
              </div>
            )}
            
            {!isAuthenticated && (
              <div className="unlock-overlay">
                <button
                  onClick={() => setShowLoginPrompt(true)}
                  className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all flex items-center space-x-2"
                >
                  <ExclamationTriangleIcon className="w-5 h-5" />
                  <span>로그인하고 취약점 분석 확인하기</span>
                </button>
              </div>
            )}
          </div>

          {/* 강점 분석 */}
          <div className={`bg-white rounded-2xl shadow-xl p-8 relative ${!isAuthenticated ? 'analysis-locked' : ''}`}>
            <h3 className="text-xl font-bold mb-6 flex items-center">
              <CheckCircleIcon className="w-6 h-6 mr-3 text-green-500" />
              강점 분석
            </h3>
            
            {isAuthenticated && aiAnalysis ? (
              <div>
                <div className="bg-green-50 rounded-xl p-4 mb-4">
                  <p className="font-semibold text-green-800 mb-2">
                    가장 강한 과목: {aiAnalysis.strongestSubject.subject}
                  </p>
                  <p className="text-green-600">
                    정답률 {aiAnalysis.strongestSubject.percentage}%
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    🎯 추천: 심화 문제로 실력을 더 높이세요
                  </p>
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700">문제 풀이 속도:</p>
                  <p className="text-gray-600">
                    평균 {aiAnalysis.speedAnalysis.avgTime}초/문제
                  </p>
                  <p className="text-sm text-gray-600">
                    {aiAnalysis.speedAnalysis.comparison}
                  </p>
                  <p className="text-sm text-orange-600 font-medium mt-2">
                    💡 {aiAnalysis.speedAnalysis.advice}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-4">
                  <LockClosedIcon className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-gray-600 mb-2">💪 당신의 강점을 분석</p>
                <p className="text-sm text-gray-500">잘하는 과목과 속도 개선법이...</p>
              </div>
            )}
            
            {!isAuthenticated && (
              <div className="unlock-overlay">
                <button
                  onClick={() => setShowLoginPrompt(true)}
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all flex items-center space-x-2"
                >
                  <CheckCircleIcon className="w-5 h-5" />
                  <span>로그인하고 강점 분석 확인하기</span>
                </button>
              </div>
            )}
          </div>

          {/* 맞춤 학습 계획 */}
          <div className={`bg-white rounded-2xl shadow-xl p-8 relative ${!isAuthenticated ? 'analysis-locked' : ''}`}>
            <h3 className="text-xl font-bold mb-6 flex items-center">
              <CalendarDaysIcon className="w-6 h-6 mr-3 text-blue-500" />
              AI 맞춤 학습 계획
            </h3>
            
            {isAuthenticated && aiAnalysis ? (
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">예상 합격 기간</p>
                      <p className="text-xl font-bold text-blue-800">
                        {aiAnalysis.studyPlan.estimatedDays}일
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">일일 목표</p>
                      <p className="text-xl font-bold text-blue-800">
                        {aiAnalysis.studyPlan.dailyGoal}문제
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <p className="font-semibold text-gray-700 mb-2">추천 학습 일정</p>
                  <p className="text-gray-600">{aiAnalysis.studyPlan.recommendedSchedule}</p>
                </div>
                
                <div>
                  <p className="font-semibold text-gray-700 mb-2">학습 우선순위</p>
                  <ol className="list-decimal list-inside space-y-1">
                    {aiAnalysis.studyPlan.prioritySubjects.map((subject, idx) => (
                      <li key={idx} className="text-gray-600">{subject}</li>
                    ))}
                  </ol>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-4">
                  <LockClosedIcon className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-gray-600 mb-2">📅 합격까지 정확한 계획</p>
                <p className="text-sm text-gray-500">예상 합격 기간과 일일 목표가...</p>
              </div>
            )}
            
            {!isAuthenticated && (
              <div className="unlock-overlay">
                <button
                  onClick={() => setShowLoginPrompt(true)}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transition-all flex items-center space-x-2"
                >
                  <CalendarDaysIcon className="w-5 h-5" />
                  <span>로그인하고 학습 계획 확인하기</span>
                </button>
              </div>
            )}
          </div>

          {/* 학습 조언 */}
          <div className={`bg-white rounded-2xl shadow-xl p-8 relative ${!isAuthenticated ? 'analysis-locked' : ''}`}>
            <h3 className="text-xl font-bold mb-6 flex items-center">
              <LightBulbIcon className="w-6 h-6 mr-3 text-yellow-500" />
              AI 학습 조언
            </h3>
            
            {isAuthenticated && aiAnalysis ? (
              <div className="space-y-3">
                {aiAnalysis.recommendations.map((recommendation, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <span className="text-orange-500 mt-1">•</span>
                    <p className="text-gray-700">{recommendation}</p>
                  </div>
                ))}
                
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-4 mt-4">
                  <p className="text-sm font-semibold text-gray-800 mb-1">
                    💡 Pro Tip
                  </p>
                  <p className="text-sm text-gray-600">
                    매일 꾸준한 학습이 가장 중요합니다. 
                    하루 {aiAnalysis.studyPlan.dailyGoal}문제씩 풀면서 
                    오답노트를 작성하세요.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-4">
                  <LockClosedIcon className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-gray-600 mb-2">💡 AI 맞춤 학습 조언</p>
                <p className="text-sm text-gray-500">개인별 학습 전략과 Pro Tip이...</p>
              </div>
            )}
            
            {!isAuthenticated && (
              <div className="unlock-overlay">
                <button
                  onClick={() => setShowLoginPrompt(true)}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-yellow-600 hover:to-orange-600 transition-all flex items-center space-x-2"
                >
                  <LightBulbIcon className="w-5 h-5" />
                  <span>로그인하고 AI 조언 확인하기</span>
                </button>
              </div>
            )}
          </div>
        </div>


        {/* 학습 시작 버튼 */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/exams')}
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-orange-600 hover:to-orange-700 transition-all"
          >
            <BookOpenIcon className="w-6 h-6" />
            <span>본격적인 학습 시작하기</span>
          </button>
        </div>
      </div>

      {/* 로그인 프롬프트 모달 */}
      {showLoginPrompt && (
        <LoginPromptManager
          userProgress={{
            solvedQuestions: results.totalQuestions,
            wrongAnswers: results.totalQuestions - results.correctCount,
            sessionMinutes: Math.floor(results.totalTime / 60)
          }}
          isLoggedIn={isAuthenticated}
          onLogin={handleLoginClick}
          onClose={() => setShowLoginPrompt(false)}
          forceShow={showLoginPrompt}
          isDiagnosticMode={true}
        />
      )}
    </div>
  );
};

export default DiagnosticResults;